/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Fragment, useEffect, useState } from "react";
import { FaRegCopy } from "react-icons/fa";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { ArchiveAPI } from "../../api/Archive";
import { UserAtom } from "../../atoms/UserAtom";
import { UserEmailArchiveAtom } from "../../atoms/UserEmailArchiveAtom";
import { IArchive } from "../../generated/Api";
import { CenterCSS, notSelect } from "../../styles/general";
import { IOption } from "../../types/CommonTypes";
import { User } from "../../types/UserTypes";
import UpdateArchiveTitle from "../Modal/container/UpdateArchiveTItle";
import { useModal } from "../Modal/Modal";
import StyledAutocompleteSelect from "../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../StyledButton";
import Switch from "../Switch";

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;
`;

const LabelText = styled.div`
  color: #222222;
  font-weight: 400;
  font-size: 16px;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1140px) {
    grid-gap: 32px;
  }

  .styled-auto-complete-wrapper {
    width: fit-content;
  }

  .grid-wrapper {
    display: grid;
    grid-template-columns: 280px 1fr;
    grid-gap: 32px;

    @media screen and (max-width: 1140px) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      grid-gap: 16px;
    }
  }

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }
`;

const ArchiveUrl = styled.div`
  margin-top: 13px;
  display: flex;
  height: 40px;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const CopyIconContainer = styled.div`
  cursor: pointer;
  padding: 13px;
  color: #fb972e;
  border-left: 1px solid #ccc;
  ${CenterCSS}
`;

const ARCHIVE_PERIODS: IArchive["period"][] = [10, 30, 60, 90, 180, 365, 9999];
const ARCHIVE_PERIODS_LABELS: string[] = [
  "10 days",
  "30 days",
  "60 days",
  "90 days",
  "6 months",
  "1 year",
  "All my emails",
];

export default function ArchiveConfig() {
  const [user, setUser] = useRecoilState(UserAtom);
  const [userEmailArchive, setUserEmailArchive] =
    useRecoilState(UserEmailArchiveAtom);
  const [autoCompleteSelectOption, setAutoCompleteOption] = useState<IOption>({
    value: "",
    name: "",
  });
  const modal = useModal();

  useEffect(() => {
    if (!user) return;
    (async () => {
      let archiveResponse = await ArchiveAPI.getDetail(user.userUid);
      let archive = archiveResponse;

      setUserEmailArchive(archive);

      let archiveLabelIndex = ARCHIVE_PERIODS.indexOf(archive.period);

      if (archiveLabelIndex !== -1)
        setAutoCompleteOption({
          name: ARCHIVE_PERIODS_LABELS[archiveLabelIndex],
          value: `${ARCHIVE_PERIODS[archiveLabelIndex]}`,
        });
    })();
  }, []);

  async function updateEmailArchive(archive: IArchive) {
    let updatedArchiveResponse = await ArchiveAPI.update({
      userUid: user.userUid,
      archive,
    });

    let updatedArchive = updatedArchiveResponse;

    if (!updatedArchive) {
      toast("Error when updating the settings", { type: "error" });

      return;
    }

    setUserEmailArchive(updatedArchive as IArchive);
  }

  const emailArchiveOptions = ARCHIVE_PERIODS.map((p, index) => ({
    name: ARCHIVE_PERIODS_LABELS[index],
    value: `${ARCHIVE_PERIODS[index]}`,
  }));

  function setValueInEmailOptions(key: keyof User["emailOptions"], value: any) {
    setUser({
      ...user,
      emailOptions: {
        ...user.emailOptions,
        [key]: value,
      },
    });
  }

  return (
    <Fragment>
      <SpaceBetween>
        <div className="grid-wrapper">
          <SecondaryTitle>Enable Public Archive</SecondaryTitle>
        </div>

        <Switch
          toggled={userEmailArchive ? userEmailArchive.enabled : false}
          whenToggled={(vle) => {
            if (userEmailArchive) {
              let newEmailArchiveOptions = {
                ...userEmailArchive,
                enabled: !userEmailArchive.enabled,
              };
              setUserEmailArchive(newEmailArchiveOptions);
              updateEmailArchive(newEmailArchiveOptions);
            }
          }}
        />
      </SpaceBetween>

      {userEmailArchive && userEmailArchive.enabled && (
        <>
          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>
                Publish emails to the archive by default?
              </SecondaryTitle>
            </div>

            <Switch
              toggled={user.emailOptions.showOnArchive}
              whenToggled={(vle) =>
                setValueInEmailOptions("showOnArchive", vle)
              }
              texts={["No", "Yes"]}
            />
          </SpaceBetween>

          <SpaceBetween>
            <div className="grid-wrapper">
              <SecondaryTitle>Archive Title & URL</SecondaryTitle>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <LabelText>{userEmailArchive.title}</LabelText>
                <ArchiveUrl>
                  <span
                    css={css`
                      padding: 9px 15px;
                      background-color: #f7f7f7;
                      ${notSelect}
                    `}
                  >{`archive.berserkermail.com/${user.userAppName}`}</span>
                  <CopyIconContainer
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `archive.berserkermail.com/${user.userAppName}`
                      );

                      //That's not explicit in the design, but I thought it was necessary for the UX
                      toast("URL successfully copied to the clipboard!", {
                        type: "success",
                      });
                    }}
                  >
                    <FaRegCopy />
                  </CopyIconContainer>
                </ArchiveUrl>
              </div>
            </div>

            <StyledButton
              inverted
              css={css`
                width: 100px;
              `}
              onClick={() => {
                modal.pushModal(
                  <UpdateArchiveTitle />,
                  "Update Your Public Archive Title & URL"
                );
              }}
            >
              Edit
            </StyledButton>
          </SpaceBetween>

          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Duration</SecondaryTitle>
              <MainText>
                Select how many days should show on your public archive.
              </MainText>
            </div>

            <div
              css={css`
                width: 160px;
                height: auto;
                > div > div > input {
                  font-size: 16px;
                }
                > div > div > input::placeholder {
                  font-size: 16px;
                }
              `}
            >
              <StyledAutocompleteSelect
                selectedOption={autoCompleteSelectOption}
                onOptionSelect={(option) => {
                  setAutoCompleteOption(option);
                  if (option.name === "") return;
                  updateEmailArchive({
                    ...userEmailArchive,
                    period: parseInt(option.value) as IArchive["period"],
                  });
                }}
                placeholder="Select"
                options={emailArchiveOptions}
              />
            </div>
          </SpaceBetween>

          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Show Opt-in Form</SecondaryTitle>
              <MainText>
                Allow people to join your list from your public archive.
              </MainText>
            </div>

            <Switch
              toggled={userEmailArchive.showOptInForm}
              whenToggled={(vle) => {
                if (userEmailArchive)
                  updateEmailArchive({
                    ...userEmailArchive,
                    showOptInForm: !userEmailArchive.showOptInForm,
                  });
              }}
            />
          </SpaceBetween>
        </>
      )}
    </Fragment>
  );
}
